import { Input } from '../ui/input'
import { MeasurementField } from '../ui/measurement'
import {
  DEFAULT_WEEK_DISTRIBUTION_CURVE,
  DEFAULT_PLAN_LIMITS,
  type PlanGenerationConfigDto,
  type CohortWeeklyMinimumsDto,
  type DifficultyMultiplierTableDto,
  type MilestoneSplitConfigDto,
  type WorkoutGradeCapTableDto,
  type DurabilityTtlWeightsDto,
  type DurabilityWeekOverWeekCapsDto
} from '../../lib/api/planGeneration'

/** Short plain-English explainer shown at the top of each config section. */
const SectionExplainer = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">{children}</p>
)

export type PlanConfigFormFieldsProps = {
  /** The config being edited. The component is fully controlled and never mutates this. */
  value: PlanGenerationConfigDto
  /** Called with the next config whenever any field changes. */
  onChange: (next: PlanGenerationConfigDto) => void
  /** When true, renders the plain-English explainer paragraph above each section. */
  showExplainers?: boolean
}

/**
 * Shared, controlled form for every tunable on PlanGenerationConfigDto
 * (everything except id/version/name/isActive/notes/audit metadata).
 * Used by both the Generation Config editor and the Plan Generation Tester's
 * config override, so the two can never drift apart again.
 *
 * No fetching and no save logic — purely value in, next value out.
 */
export const PlanConfigFormFields = ({ value, onChange, showExplainers = false }: PlanConfigFormFieldsProps) => {
  // Patch helpers (value is expected to be normalized via withPlanConfigDefaults,
  // but read with ?? fallbacks so configs saved before these fields existed don't crash)
  const patch = (p: Partial<PlanGenerationConfigDto>) => onChange({ ...value, ...p })
  const patchCohort = (p: Partial<CohortWeeklyMinimumsDto>) =>
    onChange({ ...value, cohortWeeklyMinimums: { ...value.cohortWeeklyMinimums, ...p } })
  const patchDifficulty = (p: Partial<DifficultyMultiplierTableDto>) =>
    onChange({ ...value, difficultyMultipliers: { ...value.difficultyMultipliers, ...p } })
  const patchMilestoneSplits = (p: Partial<MilestoneSplitConfigDto>) =>
    onChange({ ...value, milestoneSplits: { ...value.milestoneSplits, ...p } })
  const patchGradeCaps = (p: Partial<WorkoutGradeCapTableDto>) =>
    onChange({ ...value, gradeCaps: { ...value.gradeCaps, ...p } })
  const patchTtlWeights = (p: Partial<DurabilityTtlWeightsDto>) =>
    onChange({ ...value, durabilityTtlWeights: { ...value.durabilityTtlWeights, ...p } })
  const patchWowCaps = (p: Partial<DurabilityWeekOverWeekCapsDto>) =>
    onChange({ ...value, durabilityWeekOverWeekCaps: { ...value.durabilityWeekOverWeekCaps, ...p } })
  const patchCurveValue = (index: number, curveValue: number) => {
    const curve = [...(value.weekDistributionCurve ?? DEFAULT_WEEK_DISTRIBUTION_CURVE)]
    curve[index] = curveValue
    onChange({ ...value, weekDistributionCurve: curve })
  }

  return (
    <>
      <div className="rounded-md bg-muted/50 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Weekly Constraints</p>
        {showExplainers && (
          <SectionExplainer>
            Weekly distance keeps a hard ceiling (default 20 mi). Elevation keeps its minimum floor, while ordinary
            non-milestone workouts are capped after rounding to avoid unsafe single-session concentration.
          </SectionExplainer>
        )}
        <div className="grid gap-3 sm:grid-cols-5">
          <MeasurementField
            label="Min Dist"
            labelClassName="text-[10px] text-muted-foreground"
            kind="distance"
            valueMetric={value.weeklyMinDistanceKm}
            emptyValue={0}
            onValueMetricChange={(v) => patch({ weeklyMinDistanceKm: v ?? 0 })}
          />
          <MeasurementField
            label="Max Dist"
            labelClassName="text-[10px] text-muted-foreground"
            kind="distance"
            valueMetric={value.weeklyMaxDistanceKm}
            emptyValue={0}
            onValueMetricChange={(v) => patch({ weeklyMaxDistanceKm: v ?? 0 })}
          />
          <MeasurementField
            label="Min Elev"
            labelClassName="text-[10px] text-muted-foreground"
            kind="elevation"
            valueMetric={value.weeklyMinElevationGainMeters}
            emptyValue={0}
            onValueMetricChange={(v) => patch({ weeklyMinElevationGainMeters: v ?? 0 })}
          />
          <MeasurementField
            label="Max Workout Elev"
            labelClassName="text-[10px] text-muted-foreground"
            kind="elevation"
            valueMetric={value.maxNonMilestoneWorkoutElevationMeters ?? DEFAULT_PLAN_LIMITS.maxNonMilestoneWorkoutElevationMeters}
            emptyValue={0}
            onValueMetricChange={(v) => patch({ maxNonMilestoneWorkoutElevationMeters: v ?? 0 })}
          />
          <MeasurementField
            label="Protective Max Elev"
            labelClassName="text-[10px] text-muted-foreground"
            kind="elevation"
            valueMetric={value.maxNonMilestoneWorkoutElevationMetersForProtectiveBias ?? DEFAULT_PLAN_LIMITS.maxNonMilestoneWorkoutElevationMetersForProtectiveBias}
            emptyValue={0}
            onValueMetricChange={(v) => patch({ maxNonMilestoneWorkoutElevationMetersForProtectiveBias: v ?? 0 })}
          />
        </div>
      </div>

      <div className="rounded-md bg-muted/50 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Week Distribution Curve</p>
        {showExplainers && (
          <SectionExplainer>
            Multipliers applied to the average weekly volume to shape the final 12-week build: values &lt; 1 make a
            week easier, &gt; 1 harder. The curve is stretched/compressed for plans that aren&apos;t exactly 12
            weeks. Week 1 is always forced to be the lowest build week so plans never start harder than week 2+.
            The last values form the taper.
          </SectionExplainer>
        )}
        <div className="grid grid-cols-4 gap-3 sm:grid-cols-6">
          {(value.weekDistributionCurve ?? DEFAULT_WEEK_DISTRIBUTION_CURVE).map((curveValue, index) => (
            <label key={index} className="space-y-1">
              <span className="text-[10px] text-muted-foreground">Week {index + 1}</span>
              <Input
                type="number"
                step={0.05}
                min={0}
                value={curveValue}
                onChange={e => patchCurveValue(index, parseFloat(e.target.value) || 0)}
              />
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-md bg-muted/50 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Age Cohort Minimums</p>
        {showExplainers && (
          <SectionExplainer>
            Users are cohorted by age (under/over the threshold). For extended plans (more than 12 weeks until the
            hike), the extra &quot;pre-base&quot; weeks before the final 12-week build must hit at least these
            weekly volumes — under-40: 9 mi / 1,900 ft; over-40: 7 mi / 1,400 ft by default. These floors supersede
            the distribution curve. Users with no elevation access keep zero elevation.
          </SectionExplainer>
        )}
        <div className="grid gap-3 sm:grid-cols-5">
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Age Threshold (yrs)</span>
            <Input
              type="number"
              min={18}
              max={100}
              value={value.cohortWeeklyMinimums?.ageThresholdYears ?? 40}
              onChange={e => patchCohort({ ageThresholdYears: parseInt(e.target.value) || 40 })}
            />
          </label>
          <MeasurementField
            label="U40 Min Dist"
            labelClassName="text-[10px] text-muted-foreground"
            kind="distance"
            valueMetric={value.cohortWeeklyMinimums?.under40MinWeeklyDistanceKm ?? 14.5}
            emptyValue={0}
            onValueMetricChange={(v) => patchCohort({ under40MinWeeklyDistanceKm: v ?? 0 })}
          />
          <MeasurementField
            label="U40 Min Elev"
            labelClassName="text-[10px] text-muted-foreground"
            kind="elevation"
            valueMetric={value.cohortWeeklyMinimums?.under40MinWeeklyElevationMeters ?? 579}
            emptyValue={0}
            onValueMetricChange={(v) => patchCohort({ under40MinWeeklyElevationMeters: v ?? 0 })}
          />
          <MeasurementField
            label="O40 Min Dist"
            labelClassName="text-[10px] text-muted-foreground"
            kind="distance"
            valueMetric={value.cohortWeeklyMinimums?.over40MinWeeklyDistanceKm ?? 11.3}
            emptyValue={0}
            onValueMetricChange={(v) => patchCohort({ over40MinWeeklyDistanceKm: v ?? 0 })}
          />
          <MeasurementField
            label="O40 Min Elev"
            labelClassName="text-[10px] text-muted-foreground"
            kind="elevation"
            valueMetric={value.cohortWeeklyMinimums?.over40MinWeeklyElevationMeters ?? 427}
            emptyValue={0}
            onValueMetricChange={(v) => patchCohort({ over40MinWeeklyElevationMeters: v ?? 0 })}
          />
        </div>
      </div>

      <div className="rounded-md bg-muted/50 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Difficulty Multipliers</p>
        {showExplainers && (
          <SectionExplainer>
            Converts the goal hike&apos;s hardest days into total plan volume. The user&apos;s endurance (E) and
            hiking (H) frequency answers map to levels 1–4 (1 = least active, 4 = most active). The LOWER (less active)
            of the two levels picks the row, and that row&apos;s multipliers apply to both total distance and total
            elevation. Raising a row makes plans for those users harder.
          </SectionExplainer>
        )}
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {([
            {
              level: 1,
              activity: 'Least active',
              answers: 'H: few times / never · E: occasionally / never',
              distanceField: 'level1Distance',
              elevationField: 'level1Elevation'
            },
            {
              level: 2,
              activity: 'Light activity',
              answers: 'H: 1–3 days/month · E: 1 day/week or less',
              distanceField: 'level2Distance',
              elevationField: 'level2Elevation'
            },
            {
              level: 3,
              activity: 'Regular activity',
              answers: 'H: 1–2 days/week · E: 2–3 days/week',
              distanceField: 'level3Distance',
              elevationField: 'level3Elevation'
            },
            {
              level: 4,
              activity: 'Most active',
              answers: 'H: 3+ days/week · E: 4+ days/week',
              distanceField: 'level4Distance',
              elevationField: 'level4Elevation'
            }
          ] as const).map(band => (
            <div key={band.level} className="rounded-md border border-border/70 bg-background/60 p-3">
              <div className="mb-3">
                <p className="text-xs font-semibold text-foreground">Level {band.level}</p>
                <p className="text-[10px] font-medium text-muted-foreground">{band.activity}</p>
                <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground">{band.answers}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {([
                  ['Distance', band.distanceField],
                  ['Elevation', band.elevationField]
                ] as const).map(([label, field]) => (
                  <label key={field} className="space-y-1">
                    <span className="text-[10px] text-muted-foreground">{label}</span>
                    <Input
                      type="number"
                      step={0.1}
                      min={0}
                      value={value.difficultyMultipliers?.[field] ?? 0}
                      onChange={e => patchDifficulty({ [field]: parseFloat(e.target.value) || 0 })}
                    />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-md bg-muted/50 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Workout Fractions</p>
        {showExplainers && (
          <SectionExplainer>
            True Elev sets the minimum fraction (0–1) of weekly workouts using real outdoor elevation (stairs,
            hills, trails) instead of machines. Pack applies to main distance/elevation training days each week,
            rounded up: 0.5 selects 3 of 6 training days, or 2 of 3. Separate lunges, balance, and HIIT workout
            cards do not increase that count. No pack days are selected when the weekly pack-weight target is
            zero. Knee-issue variants are lower to reduce joint stress.
          </SectionExplainer>
        )}
        <div className="grid gap-3 sm:grid-cols-4">
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">True Elev (default)</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.trueElevationMinFractionDefault}
              onChange={e => patch({ trueElevationMinFractionDefault: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">True Elev (knee)</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.trueElevationMinFractionKneeIssues}
              onChange={e => patch({ trueElevationMinFractionKneeIssues: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Pack (default)</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.packWorkoutFractionDefault}
              onChange={e => patch({ packWorkoutFractionDefault: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Pack (knee)</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.packWorkoutFractionKneeIssues}
              onChange={e => patch({ packWorkoutFractionKneeIssues: parseFloat(e.target.value) || 0 })}
            />
          </label>
        </div>
      </div>

      <div className="rounded-md bg-muted/50 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Durability Caps</p>
        {showExplainers && (
          <SectionExplainer>
            Limits how fast volume can grow week-over-week. TTL uses cohort-specific thresholds and creates
            repayable distance/elevation debt; standalone elevation is diagnostic for goal-hike plans, while pack
            progression stays conservative.
          </SectionExplainer>
        )}
        <div className="grid gap-3 sm:grid-cols-4">
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">U40 No-Joint TTL</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityWeekOverWeekCaps?.defaultTtlMaxIncreaseFraction ?? 0.25}
              onChange={e => patchWowCaps({ defaultTtlMaxIncreaseFraction: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">U40 Joint TTL</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityWeekOverWeekCaps?.under40JointTtlMaxIncreaseFraction ?? 0.20}
              onChange={e => patchWowCaps({ under40JointTtlMaxIncreaseFraction: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">40+ No-Joint TTL</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityWeekOverWeekCaps?.age40PlusTtlMaxIncreaseFraction ?? 0.20}
              onChange={e => patchWowCaps({ age40PlusTtlMaxIncreaseFraction: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">40+ Joint TTL</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityWeekOverWeekCaps?.age40PlusJointTtlMaxIncreaseFraction ?? 0.15}
              onChange={e => patchWowCaps({ age40PlusJointTtlMaxIncreaseFraction: parseFloat(e.target.value) || 0 })}
            />
          </label>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-4">
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Elev Comfort Cap</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityWeekOverWeekCaps?.elevationMaxIncreaseFraction ?? 0.20}
              onChange={e => patchWowCaps({ elevationMaxIncreaseFraction: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Pack Step Cap</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityWeekOverWeekCaps?.packStepMaxIncreaseFraction ?? 0.10}
              onChange={e => patchWowCaps({ packStepMaxIncreaseFraction: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Debt Prev Spike</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityWeekOverWeekCaps?.elevationDebtSpikePreviousWeekMaxIncreaseFraction ?? 0.50}
              onChange={e => patchWowCaps({ elevationDebtSpikePreviousWeekMaxIncreaseFraction: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Debt Orig Spike</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityWeekOverWeekCaps?.elevationDebtSpikeOriginalWeekMaxIncreaseFraction ?? 0.25}
              onChange={e => patchWowCaps({ elevationDebtSpikeOriginalWeekMaxIncreaseFraction: parseFloat(e.target.value) || 0 })}
            />
          </label>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Protective Prev Spike</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityWeekOverWeekCaps?.protectiveElevationDebtSpikePreviousWeekMaxIncreaseFraction ?? 0.35}
              onChange={e => patchWowCaps({ protectiveElevationDebtSpikePreviousWeekMaxIncreaseFraction: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Protective Orig Spike</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityWeekOverWeekCaps?.protectiveElevationDebtSpikeOriginalWeekMaxIncreaseFraction ?? 0.20}
              onChange={e => patchWowCaps({ protectiveElevationDebtSpikeOriginalWeekMaxIncreaseFraction: parseFloat(e.target.value) || 0 })}
            />
          </label>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Over-40 Weight</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityTtlWeights?.o40Weight ?? 0.45}
              onChange={e => patchTtlWeights({ o40Weight: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Elevation Weight</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityTtlWeights?.elevationWeight ?? 0.35}
              onChange={e => patchTtlWeights({ elevationWeight: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Pack Weight</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.durabilityTtlWeights?.packWeight ?? 0.20}
              onChange={e => patchTtlWeights({ packWeight: parseFloat(e.target.value) || 0 })}
            />
          </label>
        </div>
      </div>

      <div className="rounded-md bg-muted/50 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Milestone Settings</p>
        {showExplainers && (
          <SectionExplainer>
            Milestone workouts simulate the goal hike&apos;s hardest day at increasing percentages. They are
            scheduled relative to the LAST week of the plan (N−8 through N−1, configured on the Milestone
            Schedule tab of this page) and never before the minimum week set here. Elevation-focused milestones are split into multiple
            same-day workouts: below the 2-workout threshold → 2 workouts, below the 3-workout threshold → 3,
            above → 4. The lead workout gets the lead fraction (default 30%) of the elevation as a
            hike/run/hill-repeats/stairs/step-ups workout; the rest is divided equally, preferring the
            user&apos;s selected true-elevation types over machines.
          </SectionExplainer>
        )}
        <div className="grid gap-3 sm:grid-cols-4">
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Min Milestone Week</span>
            <Input
              type="number"
              min={1}
              max={20}
              value={value.milestoneMinimumWeek ?? 4}
              onChange={e => patch({ milestoneMinimumWeek: parseInt(e.target.value) || 4 })}
            />
          </label>
          <MeasurementField
            label="2-Workout Max Elev"
            labelClassName="text-[10px] text-muted-foreground"
            kind="elevation"
            valueMetric={value.milestoneSplits?.twoWorkoutMaxElevationMeters ?? 609}
            emptyValue={0}
            onValueMetricChange={(v) => patchMilestoneSplits({ twoWorkoutMaxElevationMeters: v ?? 0 })}
          />
          <MeasurementField
            label="3-Workout Max Elev"
            labelClassName="text-[10px] text-muted-foreground"
            kind="elevation"
            valueMetric={value.milestoneSplits?.threeWorkoutMaxElevationMeters ?? 1067}
            emptyValue={0}
            onValueMetricChange={(v) => patchMilestoneSplits({ threeWorkoutMaxElevationMeters: v ?? 0 })}
          />
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Lead Workout Fraction</span>
            <Input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={value.milestoneSplits?.leadWorkoutFraction ?? 0.30}
              onChange={e => patchMilestoneSplits({ leadWorkoutFraction: parseFloat(e.target.value) || 0 })}
            />
          </label>
        </div>
      </div>

      <div className="rounded-md bg-muted/50 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Treadmill Cap</p>
        {showExplainers && (
          <SectionExplainer>
            A single treadmill workout never exceeds this distance (default 10 mi). Overflow distance is added back
            as a run/walk workout the same day so the weekly distance goal still holds.
          </SectionExplainer>
        )}
        <div className="grid gap-3 sm:grid-cols-4">
          <MeasurementField
            label="Max Treadmill Dist"
            labelClassName="text-[10px] text-muted-foreground"
            kind="distance"
            valueMetric={value.treadmillMaxDistanceKm ?? DEFAULT_PLAN_LIMITS.treadmillMaxDistanceKm}
            emptyValue={0}
            onValueMetricChange={(v) => patch({ treadmillMaxDistanceKm: v ?? 0 })}
          />
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Max Incline %</span>
            <Input
              type="number"
              step={0.5}
              min={0}
              max={50}
              value={value.treadmillMaxInclinePercent ?? 12}
              onChange={e => patch({ treadmillMaxInclinePercent: parseFloat(e.target.value) || 0 })}
            />
          </label>
        </div>
      </div>

      <div className="rounded-md bg-muted/50 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Workout Grade Caps</p>
        {showExplainers && (
          <SectionExplainer>
            Maximum steepness (elevation gain per distance) for any single workout, including milestones. Set per
            fitness band — Level 1 is the least experienced cohort. When a workout would exceed its band&apos;s cap,
            the generator first adds distance (within the weekly distance ceiling), then trims elevation as a last
            resort. Set a band to 0 to disable its cap.
          </SectionExplainer>
        )}
        <div className="grid gap-3 sm:grid-cols-4">
          <MeasurementField
            label="Level 1 Max Grade"
            labelClassName="text-[10px] text-muted-foreground"
            kind="grade"
            valueMetric={value.gradeCaps?.level1MaxElevationMetersPerKm ?? 105}
            emptyValue={0}
            onValueMetricChange={(v) => patchGradeCaps({ level1MaxElevationMetersPerKm: v ?? 0 })}
          />
          <MeasurementField
            label="Level 2 Max Grade"
            labelClassName="text-[10px] text-muted-foreground"
            kind="grade"
            valueMetric={value.gradeCaps?.level2MaxElevationMetersPerKm ?? 130}
            emptyValue={0}
            onValueMetricChange={(v) => patchGradeCaps({ level2MaxElevationMetersPerKm: v ?? 0 })}
          />
          <MeasurementField
            label="Level 3 Max Grade"
            labelClassName="text-[10px] text-muted-foreground"
            kind="grade"
            valueMetric={value.gradeCaps?.level3MaxElevationMetersPerKm ?? 160}
            emptyValue={0}
            onValueMetricChange={(v) => patchGradeCaps({ level3MaxElevationMetersPerKm: v ?? 0 })}
          />
          <MeasurementField
            label="Level 4 Max Grade"
            labelClassName="text-[10px] text-muted-foreground"
            kind="grade"
            valueMetric={value.gradeCaps?.level4MaxElevationMetersPerKm ?? 190}
            emptyValue={0}
            onValueMetricChange={(v) => patchGradeCaps({ level4MaxElevationMetersPerKm: v ?? 0 })}
          />
        </div>
      </div>

      <div className="rounded-md bg-muted/50 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Timeline Defaults</p>
        {showExplainers && (
          <SectionExplainer>
            Controls the minimum available workout days users must select in the app, default plan length when the
            request does not include a duration, and the threshold for compressed-plan warnings. The top-days counts
            set how many of the goal hike&apos;s hardest distance and elevation days are used to size the plan&apos;s
            total volume targets.
          </SectionExplainer>
        )}
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Min Workout Days</span>
            <Input
              type="number"
              min={1}
              max={7}
              value={value.minimumWorkoutDaysPerWeek ?? DEFAULT_PLAN_LIMITS.minimumWorkoutDaysPerWeek}
              onChange={e => patch({ minimumWorkoutDaysPerWeek: parseInt(e.target.value) || 1 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Default Duration</span>
            <Input
              type="number"
              min={4}
              max={24}
              value={value.defaultPlanDurationWeeks}
              onChange={e => patch({ defaultPlanDurationWeeks: parseInt(e.target.value) || 12 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Min Recommended Weeks</span>
            <Input
              type="number"
              min={1}
              max={24}
              value={value.minimumRecommendedWeeks}
              onChange={e => patch({ minimumRecommendedWeeks: parseInt(e.target.value) || 8 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Top Distance Days</span>
            <Input
              type="number"
              min={1}
              max={10}
              value={value.topDistanceDaysCount}
              onChange={e => patch({ topDistanceDaysCount: parseInt(e.target.value) || 1 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-muted-foreground">Top Elevation Days</span>
            <Input
              type="number"
              min={1}
              max={10}
              value={value.topElevationDaysCount}
              onChange={e => patch({ topElevationDaysCount: parseInt(e.target.value) || 1 })}
            />
          </label>
        </div>
      </div>
    </>
  )
}
